import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Save } from "lucide-react";
import { useTimezone } from "@/contexts/TimezoneContext";
import { zoneLabel } from "@/lib/timezone";
import VoiceChannelSelect from "./VoiceChannelSelect";
import { DURATIONS, isoToWall, wallToISO } from "./meetingUtils";

const LBL = "text-[10px] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)]";
const INPUT = "w-full mt-1 p-2 bg-black/40 border border-[var(--ff-border)] text-sm";

function initial(editing, tz) {
  if (editing) {
    const w = isoToWall(editing.start_at, tz);
    return { title: editing.title, agenda: editing.agenda || "", date: w.date, time: w.time, duration: editing.duration_minutes,
      vc_id: editing.room?.is_default ? "" : editing.room?.channel_id || "", vc_name: editing.room?.is_default ? "" : editing.room?.channel_name || "",
      notify: editing.notify !== false };
  }
  return { title: "", agenda: "", date: "", time: "", duration: 60, vc_id: "", vc_name: "", notify: true };
}

export default function MeetingForm({ editing, defaultRoom, onClose, onSaved }) {
  const { timezone } = useTimezone();
  const [f, setF] = useState(() => initial(editing, timezone));
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const timeChanged = editing && (f.date !== initial(editing, timezone).date || f.time !== initial(editing, timezone).time);

  const submit = async () => {
    if (!f.title.trim() || !f.date || !f.time) { toast.error("Add a title, day and time"); return; }
    setSaving(true);
    const body = { title: f.title.trim(), agenda: f.agenda, start_at: wallToISO(f.date, f.time, timezone), duration_minutes: Number(f.duration),
      vc_channel_id: f.vc_id, vc_channel_name: f.vc_name, notify: f.notify };
    try {
      if (editing) await api.put(`/admin/meetings/${editing.id}`, body);
      else await api.post("/admin/meetings", body);
      toast.success(editing ? (timeChanged ? "Meeting moved — everyone was asked again" : "Meeting updated") : (f.notify ? "Meeting scheduled — DMs are going out" : "Meeting scheduled"));
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save the meeting");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="meeting-form-modal">
      <div className="w-full max-w-2xl bg-[var(--ff-surface)] border border-[var(--ff-border)] my-8">
        <div className="flex items-center justify-between p-5 border-b border-[var(--ff-border)]">
          <div>
            <div className="label-eyebrow text-[var(--ff-red)]">▮ {editing ? "EDIT" : "NEW"} STAFF MEETING</div>
            <h3 className="font-display text-2xl mt-1">{editing ? "Edit Meeting" : "Schedule Meeting"}</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-black/40" data-testid="meeting-form-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-5">
          <label className="block"><span className={LBL}>Title</span>
            <input value={f.title} onChange={set("title")} maxLength={120} placeholder="e.g. Monthly host check-in" className={INPUT} data-testid="meeting-form-title" />
          </label>
          <div className="grid sm:grid-cols-3 gap-3">
            <label className="block"><span className={LBL}>Day</span>
              <input type="date" value={f.date} onChange={set("date")} className={INPUT} data-testid="meeting-form-date" />
            </label>
            <label className="block"><span className={LBL}>Start time</span>
              <input type="time" value={f.time} onChange={set("time")} className={INPUT} data-testid="meeting-form-time" />
            </label>
            <label className="block"><span className={LBL}>Length</span>
              <select value={f.duration} onChange={set("duration")} className={INPUT} data-testid="meeting-form-duration">
                {DURATIONS.map((d) => <option key={d} value={d}>{d < 60 ? `${d} min` : `${d / 60} hr${d > 60 ? "s" : ""}`}</option>)}
              </select>
            </label>
          </div>
          <div className="font-mono-ui text-[0.65rem] text-[var(--ff-text-dim)] -mt-3" data-testid="meeting-form-tz">Times are in {zoneLabel(timezone)}. Everyone sees it in their own time zone.</div>
          {timeChanged && <div className="text-xs text-[#FFD60A]" data-testid="meeting-form-reschedule-note">Changing the time clears everyone&apos;s answers and DMs them the new time.</div>}
          <label className="block"><span className={LBL}>Meeting room</span>
            <VoiceChannelSelect value={f.vc_id} onChange={(id, name) => setF({ ...f, vc_id: id, vc_name: name })}
              defaultLabel={`Default room${defaultRoom?.channel_name ? ` — ${defaultRoom.channel_name}` : " (not set)"}`} testId="meeting-form-room" />
          </label>
          <label className="block"><span className={LBL}>Agenda (optional)</span>
            <textarea value={f.agenda} onChange={set("agenda")} maxLength={2000} rows={3} placeholder="What will you cover?" className={INPUT} data-testid="meeting-form-agenda" />
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={f.notify} onChange={set("notify")} className="w-4 h-4 mt-0.5 accent-[var(--ff-red)]" data-testid="meeting-form-notify" />
            <span className="text-sm">Send Discord DMs to all hosts &amp; executive managers
              <span className="block font-mono-ui text-[0.65rem] text-[var(--ff-text-dim)] mt-0.5">When it&apos;s scheduled, if the time changes or it&apos;s canceled, and again just before it starts.</span>
            </span>
          </label>
        </div>
        <div className="flex justify-end gap-3 p-5 border-t border-[var(--ff-border)]">
          <button onClick={onClose} className="btn-ghost inline-flex items-center text-xs" data-testid="meeting-form-cancel">Cancel</button>
          <button onClick={submit} disabled={saving} className="btn-primary text-xs disabled:opacity-40" data-testid="meeting-form-submit">
            <Save size={12} /> {saving ? "Saving…" : editing ? "Save changes" : "Schedule meeting"}
          </button>
        </div>
      </div>
    </div>
  );
}
